import { createFileRoute } from '@tanstack/react-router'
import { useEffect } from 'react'
import { fetchRagFiles } from '@/api/content/file'
import { contentSearchSchema } from '@/common/contentSearchSchema'
import { useProjectBreadcrumb } from '@/common/useProjectBreadcrumb'
import { RagFilePage } from '@/components/RagFilePage'
import { isConversationWorkspaceAvailable } from '@/features/conversation/availability'
import { LegacyContentSettingsBridge } from '@/features/settings/LegacyContentSettingsBridge'

export const Route = createFileRoute('/_auth/$orgSlug/$projectId/content/file/')({
  validateSearch: contentSearchSchema,
  loaderDeps: ({ search }) => ({ page: search.page, q: search.q, docId: search.docId }),
  loader: ({ params, deps, context }) =>
    isConversationWorkspaceAvailable(context.user.userOrgProfile?.enabled_features)
      ? Promise.resolve({ items: [], pageCount: 1, documentCount: 0 })
      : fetchRagFiles({
          projectId: parseInt(params.projectId, 10),
          currentPage: deps.page,
          searchText: deps.q,
          docId: deps.docId
        }),
  head: () => ({ meta: [{ title: 'Workspace Content - Snowvault' }] }),
  component: FilePage
})

function FilePage() {
  const { orgSlug, projectId } = Route.useParams()
  const { items, pageCount, documentCount } = Route.useLoaderData()
  const { page, q, docId } = Route.useSearch()
  const navigate = Route.useNavigate()
  const { projectTitle, projectTo } = useProjectBreadcrumb()
  const { userOrgProfile } = Route.useRouteContext().user
  const conversationWorkspaceAvailable = isConversationWorkspaceAvailable(userOrgProfile?.enabled_features)

  useEffect(() => {
    if (conversationWorkspaceAvailable) return
    if (items.length === 0 && page > 1) {
      void navigate({ search: { page: 1, q } })
    }
  }, [conversationWorkspaceAvailable, items.length, page, q, navigate])

  if (conversationWorkspaceAvailable) {
    return (
      <LegacyContentSettingsBridge
        orgSlug={orgSlug}
        projectId={parseInt(projectId, 10)}
        kind="files"
        {...(docId !== undefined ? { contentItemId: docId } : {})}
      />
    )
  }

  return (
    <RagFilePage
      breadcrumbs={[{ label: projectTitle, to: projectTo }, { label: 'Content' }, { label: 'Files' }]}
      items={items}
      projectId={parseInt(projectId, 10)}
      currentPage={page}
      pageCount={pageCount}
      documentCount={documentCount}
      searchText={q}
      onPageChange={(p) => void navigate({ search: { page: p, q } })}
      onSearchChange={(text) => void navigate({ search: { q: text, page: 1 } })}
      onClearDocId={docId !== undefined ? () => void navigate({ search: { page: 1, q: '' } }) : undefined}
    />
  )
}
